import BaseDrawer from "@stardust/base-drawer";
import { buildProductCard, buildProductCardSkeleton } from "@stardust/product-card-builder";
import { ProductCard } from "@stardust/product-card";
import wishlist, { WISHLIST_EVENTS, WISHLIST_DRAWER_EVENTS } from "@stardust/wishlist";

/**
 * WishlistDrawer - Slide-in drawer UI for displaying wishlist items
 *
 * Responsibilities:
 * - Open/close animation and state
 * - Rendering wishlist items as product cards, newest first
 * - Remove button UI (delegates actual removal to wishlist module), with every
 *   card locked until the re-render lands
 * - Skeleton card for the item being added
 *
 * Listens for:
 * - wishlist-drawer:open - Opens the drawer
 * - wishlist-drawer:close - Closes the drawer
 * - wishlist:updated - Refreshes items when open
 * - wishlist:add:start / wishlist:add:end - Shows and clears the add skeleton
 */
class WishlistDrawer extends BaseDrawer {
  constructor(element) {
    super(element, {
      openClass: 'opacity-0 translate-x-full',
      closeClass: 'opacity-0 translate-x-full',
    });
  }

  load() {
    // Cache DOM
    this.itemsContainer = this.element.querySelector("[data-wishlist-items-container]");
    this.emptyState = this.element.querySelector("[data-wishlist-empty]");
    this.loadingState = this.element.querySelector("[data-wishlist-loading]");

    // State
    this.locked = false;
    this.isAdding = false;
    this._isRemoving = false;
    this._loadInFlight = false;

    // Events
    document.addEventListener(WISHLIST_DRAWER_EVENTS.open, this._boundOpen);
    document.addEventListener(WISHLIST_DRAWER_EVENTS.close, this._boundClose);
    document.addEventListener(WISHLIST_EVENTS.updated, () => this.isOpen && this.loadItems());
    document.addEventListener(WISHLIST_EVENTS.addStart, () => this._onAddStart());
    document.addEventListener(WISHLIST_EVENTS.addEnd, () => this._onAddEnd());
  }

  _onAddStart() {
    this._itemCountBeforeAdd = wishlist.getUserWishList().length;
    this.isAdding = true;
    // The drawer opens at the start of an add, and the wishlist ignores removals
    // until it finishes. Lock the visible cards so a click on one reads as "busy"
    // instead of looking like it worked.
    this._setItemsLocked(true);
    this._syncSkeleton();
  }

  _onAddEnd() {
    this.isAdding = false;
    // A successful add leaves the skeleton for the in-flight render to replace, so the
    // real card lands in the same spot. With no render pending (a failed add, or one
    // that already finished), nothing else will clear it.
    if (!this._loadInFlight) {
      this._setItemsLocked(this._isRemoving);
      this._syncSkeleton();
    }
  }

  /**
   * First paint with no cards yet: one skeleton per item the wishlist already holds, so
   * the grid has its final size before the product data arrives. Only when the wishlist
   * isn't known yet (SDK still starting) or is empty does it fall back to the spinner.
   */
  _showPlaceholdersIfNothingRendered() {
    if (!this.itemsContainer || this.itemsContainer.querySelector("[data-product-card]")) return;

    // Mid-add, the list may already include the new item (a pass triggered by the
    // wishlist update), but the add skeleton stands in for it. Count only what was
    // saved before the add, or the new item gets two skeletons.
    const knownItemCount = this.isAdding
      ? this._itemCountBeforeAdd
      : wishlist.getUserWishList().length;
    if (knownItemCount === 0) {
      if (!this.itemsContainer.children.length) this._showLoading();
      return;
    }

    this.itemsContainer.innerHTML = "";
    for (let index = 0; index < knownItemCount; index++) {
      this.itemsContainer.appendChild(buildProductCardSkeleton());
    }
    this._syncSkeleton();
    this._showItems();
  }

  /**
   * Keep a skeleton card at the start of the grid exactly while an add is in flight.
   * Called after every render too, since a render replaces the cards and would drop it.
   */
  _syncSkeleton() {
    if (!this.itemsContainer) return;
    this.itemsContainer.querySelector("[data-wishlist-skeleton]")?.remove();
    if (!this.isAdding) {
      // A failed first add leaves the grid empty; fall back to the empty state.
      if (!this.itemsContainer.children.length) this._showEmpty();
      return;
    }
    const skeleton = buildProductCardSkeleton();
    skeleton.setAttribute("data-wishlist-skeleton", "");
    // Newest first, so the item being added lands at the top.
    this.itemsContainer.prepend(skeleton);
    this._showItems();
  }

  // Hook called when drawer opens
  onOpen() {
    this.loadItems();
  }

  async loadItems() {
    // Concurrent passes race: an early open awaits readyPromise, whose
    // completion fires wishlist:updated, which starts a second pass — both
    // then append into the same container (items doubled). Only the newest
    // pass may touch the DOM; stale passes bail after every await.
    const seq = (this._loadSeq = (this._loadSeq || 0) + 1);
    // Cards from an earlier pass stay on screen (dimmed and inert when locked)
    // while the list refreshes. The spinner is only for a first load with nothing
    // to show, so reopening the drawer or removing an item never blanks it.
    this._showPlaceholdersIfNothingRendered();
    this._loadInFlight = true;

    try {
      // Opened before Swym's SDK finished initializing, the snapshot is still
      // empty — wait for the initial load instead of rendering a false empty
      // state (the loading state above stays visible meanwhile).
      await wishlist.readyPromise;
      if (seq !== this._loadSeq) return;
      const items = wishlist.getUserWishList() || [];
      const itemsKey = items.map((item) => item.product_handle).join(",");

      if (items.length === 0) {
        // Drop stale cards so a later load knows there is nothing to keep showing.
        if (this.itemsContainer) this.itemsContainer.innerHTML = "";
        this._renderedItemsKey = itemsKey;
        // The first item being added: show its skeleton, not "your wishlist is empty".
        if (this.isAdding) this._syncSkeleton();
        else this._showEmpty();
      } else if (itemsKey === this._renderedItemsKey && this.itemsContainer?.querySelector("[data-product-card]")) {
        // Already showing exactly this list — no refetch, no re-render.
        this._showItems();
      } else {
        const renderedEveryItem = await this._renderItems(items, seq);
        if (seq !== this._loadSeq) return;
        // Only remember a complete render. A product fetch that failed leaves a card
        // missing, and the next pass must retry it rather than reuse what is shown.
        this._renderedItemsKey = renderedEveryItem ? itemsKey : undefined;
        this._showItems();
      }
    } catch (error) {
      if (seq !== this._loadSeq) return;
      console.error("[WishlistDrawer] Failed to load:", error);
      this._showEmpty();
    } finally {
      // Only the newest pass settles the lock; a stale pass returns before reaching
      // here with work still pending. A pass that started before a remove (e.g. the
      // refresh after an add) can finish while that remove is still waiting on Swym,
      // so it must not clear the remove's lock — re-apply it to the fresh cards instead.
      if (seq === this._loadSeq) {
        this._loadInFlight = false;
        this._setItemsLocked(this._isRemoving || this.isAdding);
        this._syncSkeleton();
      }
    }
  }

  /**
   * @returns {Promise<boolean>} true when every item produced a card
   */
  async _renderItems(items, seq) {
    if (!this.itemsContainer) return false;

    // Fetch product data
    const products = await wishlist.fetchProducts(items);

    if (seq !== this._loadSeq) return false;
    this.itemsContainer.innerHTML = '';

    // Build cards
    products.forEach((product) => {
      if (!product) return;

      const card = buildProductCard(product);

      // Replace the wishlist heart button with a remove button
      const heartBtn = card.querySelector('[data-product-card-wishlist]');
      const removeBtn = this._createRemoveButton(product.handle);
      if (heartBtn) {
        heartBtn.replaceWith(removeBtn);
      } else {
        card.style.position = 'relative';
        card.appendChild(removeBtn);
      }

      this.itemsContainer.appendChild(card);
      new ProductCard(card);
    });

    return products.every(Boolean);
  }

  _createRemoveButton(handle) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'absolute top-2 right-2 z-10 flex cursor-pointer items-center justify-center w-6 h-6 rounded-full bg-white/80 hover:bg-white shadow-sm text-gray-600 hover:text-red-600 transition-colors md:top-4 md:right-4';
    btn.setAttribute('aria-label', 'Remove from wishlist');
    btn.dataset.wishlistRemoveHandle = handle;
    btn.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    `;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      this._removeItem(handle);
    });

    return btn;
  }

  /**
   * Remove an item with every card locked, like the cart drawer does for its rows.
   *
   * Stays locked until the re-render lands (see loadItems), not just until the
   * request returns — otherwise a second click could act on a card list that is
   * about to be replaced. A successful removal fires wishlist:updated, whose
   * re-render unlocks; with no render pending (a failed removal), unlock here.
   */
  async _removeItem(handle) {
    if (this.locked) return;
    this._isRemoving = true;
    this._setItemsLocked(true);
    try {
      await wishlist.removeByHandle(handle);
    } finally {
      this._isRemoving = false;
      if (!this._loadInFlight) this._setItemsLocked(this.isAdding);
    }
  }

  /**
   * @param {boolean} isLocked
   */
  _setItemsLocked(isLocked) {
    // Callers pass flags that start out undefined (`_isRemoving || isAdding`
    // before any remove or add). toggleAttribute with an undefined force
    // flips the attribute instead of clearing it, which left the cards gray
    // on every other render, so always pass a real boolean.
    isLocked = Boolean(isLocked);
    this.locked = isLocked;
    if (!this.itemsContainer) return;
    // The dimming hangs off one attribute on the container (see
    // snippets/wishlist-drawer-items.liquid), not a class on each card: per-card
    // classes could outlive the lock and leave cards gray with the drawer unlocked.
    this.itemsContainer.toggleAttribute("data-locked", isLocked);
    for (const button of this.itemsContainer.querySelectorAll("button")) {
      if (isLocked) button.setAttribute("aria-disabled", "true");
      else button.removeAttribute("aria-disabled");
    }
  }

  // ─────────────────────────────────────────────────────────────
  // UI State Helpers
  // ─────────────────────────────────────────────────────────────

  _showLoading() {
    this.loadingState?.classList.remove("hidden");
    this.emptyState?.classList.add("hidden");
    this.itemsContainer?.classList.add("hidden");
  }

  _showEmpty() {
    this.loadingState?.classList.add("hidden");
    this.emptyState?.classList.remove("hidden");
    this.itemsContainer?.classList.add("hidden");
  }

  _showItems() {
    this.loadingState?.classList.add("hidden");
    this.emptyState?.classList.add("hidden");
    this.itemsContainer?.classList.remove("hidden");
  }
}

// ─────────────────────────────────────────────────────────────
// Init
// ─────────────────────────────────────────────────────────────

const init = () => {
  const wishlistDrawer = document.querySelector('[data-wishlist-drawer]');
  if (wishlistDrawer) new WishlistDrawer(wishlistDrawer).load();
};
init();
